import { PaymentMethod } from 'api/app/types';
import { B2BItemModel } from 'api/public/types';
import cashIconSvg from 'assets/icons/Cash.svg';
import { AddToCartFormValues } from 'features/main/routes/ItemDetails/types';

export const formatNumber = (input: number = 0): string => {
  return new Intl.NumberFormat('en-US').format(input);
};

export const formatCurrency = (input: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'VND',
  }).format(input);
};

function repeat(str: string, times: number): string {
  return new Array(times + 1).join(str);
}

export const pad = (num: number, maxLength: number): string => {
  return repeat('0', maxLength - num.toString().length) + num;
};

export function scrollToTop(
  top: ScrollToOptions['top'] = 0,
  left: ScrollToOptions['left'] = 0,
  behavior: ScrollToOptions['behavior'] = 'auto'
) {
  window.scrollTo({
    top,
    left,
    behavior,
  });
}

export const loadJS = (src: string, attr?: { [key: string]: any }) => {
  return new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.setAttribute('src', src);
    tag.setAttribute('type', 'text/javascript');
    if (attr) {
      for (var key in attr) {
        tag.setAttribute(key, attr[key]);
      }
    }
    tag.onload = resolve;

    tag.onerror = reject;

    document.head.appendChild(tag);
  });
};

export const formatTiersDisplay = (tiers: B2BItemModel[]) =>
  tiers.length > 3
    ? [...tiers.slice(0, 3), { name: `+${tiers.length - 3}` } as B2BItemModel]
    : tiers;

export const formatPriceRangeDisplay = (
  fieldName: 'price' | 'original_price',
  prices: B2BItemModel[],
  unit?: string
) => {
  const extraUnit = unit ? `/${unit}` : '';

  if (!prices) return '';

  if (prices.length > 1) {
    const sortedFieldPrices = prices
      .map((p) => p[fieldName])
      .filter((i) => !!i)
      .sort((a, b) => a - b);

    const sortPrizes = prices
      .map((p) => p.price)
      .filter((i) => !!i)
      .sort((a, b) => a - b);

    if (!sortedFieldPrices.length) {
      return '';
    }

    if (sortedFieldPrices.length === 1 && fieldName === 'original_price') {
      if (sortedFieldPrices[0] < sortPrizes[sortPrizes.length - 1]) {
        sortedFieldPrices.push(sortPrizes[sortPrizes.length - 1]);
      } else {
        sortedFieldPrices.unshift(sortPrizes[0]);
      }
    }

    return sortedFieldPrices[0] !==
      sortedFieldPrices[sortedFieldPrices.length - 1]
      ? `${formatCurrency(
          sortedFieldPrices[0]
          // Math.min(sortedFieldPrices[0], sortPrizes[0])
        )}${extraUnit} - ${formatCurrency(
          sortedFieldPrices[sortedFieldPrices.length - 1]
        )}${extraUnit}`
      : `${formatCurrency(prices[0][fieldName])}${extraUnit}`;
  }
  return `${formatCurrency(prices[0][fieldName])}${extraUnit}`;
};

export const defaultPaymentMethod = {
  code: 'cash_by_recipient',
  name_vi_vn: 'Tiền mặt',
  icon_url: cashIconSvg,
} as PaymentMethod;

export const getUniqueCartId = (values: AddToCartFormValues) =>
  `${values.store_id}_${values._id}_${values.tier_id}`;

export const generateLetterAvatar = (name: string = '') =>
  `${name.split(' ')[0] ? name.split(' ')[0][0] : ''}${
    name.split(' ')[1] ? name.split(' ')[1][0] : ''
  }`;

export const cleanAccents = (str: string): string => {
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
  str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  // Combining Diacritical Marks
  str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ''); // huyền, sắc, hỏi, ngã, nặng
  str = str.replace(/\u02C6|\u0306|\u031B/g, ''); // mũ â (ê), mũ ă, mũ ơ (ư)

  return str;
};

export const highlightSearchTextFromString = (
  searchText: string = '',
  str: string = ''
) =>
  str.replace(new RegExp(searchText, 'gi'), (match) => `<mark>${match}</mark>`);

export const uniqueArray = (arr: Array<any> = []) => {
  let uniqueArr: Array<any> = arr.filter((a, idx) => {
    return arr.indexOf(a) === idx;
  });

  return uniqueArr;
};
